/**
 * the Pending Assets collection maintans a list of assets that are in a state of flux,
 * entering or leaving a category due to import, deletion or recategorization.  We need
 * to do this parly because our main selection mechanism, searchAssetListObjects(), goes through
 * an indexed search layer that takes a moment to re-index after a change.  This collection
 * maintains state about any changes that we have performed that the DAM is not yet finished with.
 * @class
 * @TODO This collection needs a lot of attention but right now it works
 */
import Base from '@netx/core/lib/netx-collection';
import Model from '../models/pendingAsset';

export default class PendingAssets extends Base {
	/** @override */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;

		super(models, opts);

		/**
		 * Pending Assets collections react to various pre-existing system events that indicate
		 * an asset is about to be added to, or removed from, a category.
		 *
		 * It creates an 'coming' record when a file import begins,
		 */
		this.listenTo(dispatcher, 'upload:import:imported', this.assetImporting);
		/**
		 * and both 'coming' and 'going' records when assets are recategorized.
		 */
		this.listenTo(
			dispatcher,
			'asset:category:added',
			this.assetEnteringCategory,
		);
		this.listenTo(
			dispatcher,
			'asset:category:removed',
			this.assetLeavingCategory,
		);

		/**
		 * Asset deletion is almost instant, but this could be extended to that as well.
		 */
	}

	assetImporting(plupload, file, asset) {
		const categoryId = asset.get('categoryid');

		var p = new Model({
			assetId: asset.id,
			categoryIds: [categoryId],
			direction: 'coming',
			state: 'uploading',
		});

		var events = {
			done: 'asset:task:complete:',
			cancelled: 'asset:pending:import:cancelled:',
		};

		this.add(p);

		/**
		 * When an uploading asset finishes uploading, the pending list record state is switched from 'uploading' to 'pending'
		 */
		dispatcher.once(
			events.uploaded + p.id,
			function () {
				p.set({state: 'pending'});
			},
			p,
		);

		/**
		 * When a 'tasks:done' event for an asset arrives, that asset is removed from the Pending list.
		 */
		var setupDoneListener = function () {
			dispatcher.once(
				events.done + p.id,
				function () {
					if (p.collection) {
						p.collection.remove(p);
					}
					dispatcher.trigger('asset:pending:task:complete:' + p.id);
					dispatcher.trigger(
						'category:asset:pending:task:complete',
						categoryId,
						p.id,
					);
					// kill the cancel listener.
					dispatcher.off(events.cancelled + p.id, undefined, p);
				},
				p,
			);
		};

		setupDoneListener();

		/**
		 * In the case of import cancellation, that asset is also removed from the list.
		 */
		dispatcher.once(
			events.cancelled + p.id,
			function (model, newId) {
				// DAM-7713:
				// If there was a new ID issued, we want to track tasks on that ID,
				// instead of on the model's current ID.
				// (The model ought to update itself momentarily; this event is triggered right before that happens.)
				if (model && model.id !== newId) {
					var oldId = model.id;
					p.set({
						assetId: newId,
					});
					// DEVNOTE: the decision to embed the asset id in the event name makes this part hella awkward.
					// It's nice elsewhere: in the case of asset models, every asset can listen for only its ID
					// (n performance), rather than all assets listening for all IDs (n^2), and we have a lot of asset models.
					// But in this context, it'd be better to have permanent listeners to events that don't have IDs in their names,
					// that don't need to be turned on and off all the time.
					//
					// I know that in some places we trigger two different events in order to have it both ways.  That also feels yucky.
					//
					// Perhaps a single "all" listener could be in charge of spotting the form without the ID,
					// translating it to the form with the ID, and re-triggering,
					// for every event its useful to do that with.
					//
					// Until then, this sort of cruft is necessary:
					dispatcher.off(events.done + oldId); // that ID is dead, nobody should listen.
					setupDoneListener();
				} else {
					// Some other cancellation ... just leave it alone
					if (p.collection) {
						p.collection.remove(p);
					}
					// Kill all listeners
					dispatcher.off(events.uploaded + p.id, undefined, p);
					dispatcher.off(events.done + p.id, undefined, p);
				}
				// TODO: all listeners off
			},
			p,
		);

		dispatcher.trigger(
			'asset:pending:import:start',
			plupload,
			file,
			asset,
			categoryId,
		);
	}

	assetEnteringCategory(assetId, categoryId, asset) {
		var p = new Model({
			assetId: parseInt(assetId),
			categoryIds: [parseInt(categoryId)],
			direction: 'coming',
			state: 'pending',
		});

		this.add(p);

		dispatcher.once(
			'asset:task:complete:' + assetId,
			function (task) {
				if (p.collection) {
					p.collection.remove(p);
				}
				dispatcher.trigger('asset:pending:task:complete:' + assetId);
			},
			p,
		);

		dispatcher.trigger('asset:pending:category:coming', assetId, categoryId, p);
	}

	assetLeavingCategory(assetId, categoryId) {
		var p = new Model({
			assetId: parseInt(assetId),
			categoryIds: [parseInt(categoryId)],
			direction: 'going',
			state: 'pending',
		});

		this.add(p);

		dispatcher.once(
			'asset:task:complete:' + assetId,
			function (task) {
				if (p.collection) {
					p.collection.remove(p);
				}
				dispatcher.trigger('asset:pending:task:complete:' + assetId);
			},
			p,
		);

		dispatcher.trigger('asset:pending:category:going', assetId, categoryId, p);
	}

	/**
	 * The methods findByAsset, filterByAsset, findByCategory and filterByCategory
	 * take the appropriate ID as an argument, and return pendingAsset objects if they exist in the collection.
	 * The find* methods return undefined if nothing is found; the filter* methods
	 * return an empty array.
	 */

	// TODO: every render of every assetListItemView now calls findByAsset, so
	// maybe measure its performance, possibly reorg the object to get better access.

	findByAsset(assetId) {
		return this.find(function (p) {
			return parseInt(assetId) == parseInt(p.get('assetId'));
		});
	}

	filterByAsset(assetId) {
		return this.filter(function (p) {
			return parseInt(assetId) == parseInt(p.get('assetId'));
		});
	}

	findByCategory(catId) {
		return this.find(function (p) {
			return (
				_.indexOf(p.get('categoryIds'), parseInt(catId)) > -1 || // check for 37
				_.indexOf(p.get('categoryIds'), catId + '') > -1 // check for "37"
			);
		}); // TODO: refactor as array
	}

	filterByCategory(catId) {
		return this.filter(function (p) {
			return (
				_.indexOf(p.get('categoryIds'), parseInt(catId)) > -1 || // check for 37
				_.indexOf(p.get('categoryIds'), catId + '') > -1 // check for "37"
			);
		}); // TODO: refactor as array
	}

	/** @type {string} */
	static get __name__() {
		return 'PendingAssets';
	}
}
